import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { TemplateInfo } from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import { getFieldKey } from "@fartherfinance/frontend/api/TaskWorkflows/utils/fieldHelpers";
import { hasEnumData } from "@fartherfinance/frontend/api/TaskWorkflows/utils/guards";

import { fetchFieldOptionsBatch } from "../services/fieldOptionsService";
import { matchValueToOption } from "../utils/optionMatchingUtils";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

import type {
  InitialFieldDataContextValue,
  LoadingMap,
  OptionsMap,
} from "../../core/types";

const InitialFieldDataContext = createContext<InitialFieldDataContextValue>({
  optionsMap: {},
  loadingMap: {},
  allOptionsLoaded: false,
  matchInitialValue: () => undefined,
  processedInitialValues: undefined,
});

export const useInitialFieldData = () => useContext(InitialFieldDataContext);

interface InitialFieldDataProviderProps {
  children: React.ReactNode;
  template: TemplateInfo;
  initialValues?: Record<string, unknown>;
}

export const InitialFieldDataProvider: React.FC<
  InitialFieldDataProviderProps
> = ({ children, template, initialValues }) => {
  const auth = useAdvisorRequestAuth();

  const enumFields = useMemo(() => {
    return template.parameters.filter((param) => hasEnumData(param));
  }, [template.parameters]);

  const [optionsMap, setOptionsMap] = useState<OptionsMap>({});
  const [loadingMap, setLoadingMap] = useState<LoadingMap>(() => {
    const initialLoadingMap: LoadingMap = {};
    // Only set loading state for fields that will actually be loaded (have initial values)
    if (initialValues) {
      enumFields.forEach((field) => {
        const fieldKey = getFieldKey(field);
        if (fieldKey in initialValues) {
          initialLoadingMap[fieldKey] = true;
        }
      });
    }
    return initialLoadingMap;
  });

  const fieldKeysToLoad = useMemo(() => {
    if (!initialValues) {
      return [];
    }

    return enumFields
      .filter((field) => getFieldKey(field) in initialValues)
      .map((field) => getFieldKey(field));
  }, [enumFields, initialValues]);

  const allOptionsLoaded = useMemo(() => {
    if (fieldKeysToLoad.length === 0) {
      return true;
    }

    return fieldKeysToLoad.every((key) => !loadingMap[key]);
  }, [fieldKeysToLoad, loadingMap]);

  useEffect(() => {
    if (!initialValues || fieldKeysToLoad.length === 0) {
      return;
    }

    const fieldsToLoad = enumFields.filter(
      (field) => getFieldKey(field) in initialValues
    );

    if (fieldsToLoad.length === 0) {
      return;
    }

    const loadOptions = async () => {
      try {
        const batchOptions = await fetchFieldOptionsBatch(fieldsToLoad, auth);

        setOptionsMap((prev) => ({
          ...prev,
          ...batchOptions,
        }));
      } catch (error) {
        console.error(
          "[InitialFieldDataContext] Error loading options in batch:",
          error
        );
      } finally {
        setLoadingMap((prevLoadingMap) => {
          const updatedLoadingMap = { ...prevLoadingMap };
          fieldsToLoad.forEach((field) => {
            updatedLoadingMap[getFieldKey(field)] = false;
          });
          return updatedLoadingMap;
        });
      }
    };

    loadOptions();
  }, [enumFields, initialValues, fieldKeysToLoad, auth]);

  const matchInitialValue = useCallback(
    (fieldKey: string, value: unknown): unknown => {
      if (!value) {
        return value;
      }

      const field = template.parameters.find(
        (p) => getFieldKey(p) === fieldKey
      );
      if (!field || !hasEnumData(field)) {
        return value;
      }

      const options = optionsMap[fieldKey] || [];
      if (options.length === 0) {
        return value;
      }

      // Use the extracted utility function for value matching
      return matchValueToOption(value, options) || undefined;
    },
    [template.parameters, optionsMap]
  );

  const processedInitialValues = useMemo(() => {
    if (!initialValues || !allOptionsLoaded) {
      return undefined;
    }

    const processed: Record<string, unknown> = {};

    Object.entries(initialValues).forEach(([key, value]) => {
      processed[key] = matchInitialValue(key, value);
    });

    return processed;
  }, [initialValues, allOptionsLoaded, matchInitialValue]);

  const contextValue = useMemo(
    () => ({
      optionsMap,
      loadingMap,
      allOptionsLoaded,
      matchInitialValue,
      processedInitialValues,
    }),
    [
      optionsMap,
      loadingMap,
      allOptionsLoaded,
      matchInitialValue,
      processedInitialValues,
    ]
  );

  return (
    <InitialFieldDataContext.Provider value={contextValue}>
      {children}
    </InitialFieldDataContext.Provider>
  );
};
